import { TWO_STAGE_ADDED_LABEL, TWO_STAGE_REDUCED_LABEL } from '../dtc-commerce-mmm-constants';

export interface GlossaryEntry {
  term: string;
  definition: string;
}

// Client-supplied terms with "effect" renamed to "contribution" to match the
// dashboard's metric naming, standardized on "tactic", plus dashboard-specific
// terms (baseline, ROAS/CPA, saturation, decay, etc.). Alphabetical order.
export const GLOSSARY_ENTRIES: GlossaryEntry[] = [
  {
    term: 'Actualized',
    definition:
      'The spend/contribution for a given tactic after making necessary corrections for demand gen ↔ demand capture dynamics. This is the true, causal form of each figure.'
  },
  {
    term: 'Actualized Contribution',
    definition:
      'The ultimate contribution caused by a given tactic; a multi-touch accounting of contribution. For demand gen tactics, this will often be greater than the direct contribution; for demand capture tactics, it will often be less.'
  },
  {
    term: 'Actualized Spend',
    definition:
      "The amount of investment caused by a given tactic; the economist's version of spend. For demand gen tactics, this will often be greater than the direct spend; for demand capture tactics, it will often be less."
  },
  {
    term: 'Baseline / Organic',
    definition:
      'The portion of total revenue the model attributes to factors other than paid media — organic demand, seasonality, and other baseline drivers.'
  },
  {
    term: 'Contribution',
    definition:
      'The amount of the KPI (revenue or conversions) the model credits to a given tactic, as opposed to baseline/organic drivers. The dashboard reports both a Direct and an Actualized version.'
  },
  {
    term: 'Credible Interval',
    definition:
      "The range within which the true value is likely to fall given the model's uncertainty — a 90% credible interval contains the true value with 90% probability. The Bayesian analog of a confidence interval."
  },
  {
    term: 'Decay (Adstock)',
    definition:
      "The carryover of a tactic's contribution over time — how much of one week's spend keeps driving contribution in later weeks. Also called adstock. Shown in the drawer's Decay chart."
  },
  {
    term: 'Demand Capture (DC)',
    definition:
      'Strategies and tactics aimed at converting existing consumer interest in a product, in a context where the consumer was actively seeking it out. Examples would include branded search, branded shopping, etc. Shown as the DC badge on a tactic.'
  },
  {
    term: 'Demand Generation (DG)',
    definition:
      'Strategies and tactics aimed at introducing a product to a consumer, in a context where they were not actively seeking it out. Examples would include television, podcasts, social media, etc. Shown as the DG badge on a tactic.'
  },
  {
    term: 'Direct',
    definition:
      'The spend/contribution for a given tactic prior to any correction for demand gen ↔ demand capture dynamics.'
  },
  {
    term: 'Direct Contribution',
    definition:
      'The proximate contribution of a given tactic; a last-touch accounting of contribution.'
  },
  {
    term: 'Direct Spend',
    definition:
      "The amount of investment directly tied to a given tactic; the accountant's version of spend."
  },
  {
    term: TWO_STAGE_REDUCED_LABEL,
    definition:
      'Indicates spend/contribution being subtracted from a demand capture tactic, because it was ultimately caused by an upstream demand generation tactic.'
  },
  {
    term: TWO_STAGE_ADDED_LABEL,
    definition:
      'Indicates spend/contribution being added to a demand gen tactic, because it has stimulated downstream spend/contribution in a demand capture tactic.'
  },
  {
    term: 'Informative Prior',
    definition:
      "A starting assumption fed into the model from outside evidence instead of letting the model infer a tactic's effect from the marketing data alone. It anchors the estimate toward the experiment's measured result. Tactics using an incrementality test as a prior are marked with a green beaker."
  },
  {
    term: 'ROAS / CPA',
    definition:
      'Return on ad spend (revenue ÷ spend) for revenue models, or cost per acquisition (spend ÷ conversions) for conversion models — the efficiency of a tactic relative to its investment. Follows the same Direct vs Actualized distinction as the spend and contribution figures.'
  },
  {
    term: 'Saturation',
    definition:
      "The diminishing-returns relationship between spend and contribution: as spend on a tactic rises, each additional dollar tends to drive less incremental contribution. Shown in the drawer's Saturation chart."
  },
  {
    term: 'Two-Stage Decomposition',
    definition:
      'The modeling step that reattributes credit between demand generation and demand capture tactics, turning Direct figures into Actualized ones. Its result appears as the Added and Reduced amounts on the contribution bars.'
  }
];
